import * as React from 'react';
import { OnUpdateEvent, SubscriberService, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import ZenDataService from "./zen-data-service";
import { blazorServiceInstance } from "./blazor-service";
import { svgEditServiceInstance } from "./svg-edit-service";
import { coloursServiceInstance } from "./colours-service";
import { blazorConfirm } from "@/common/utilities";
import { startDownloadContent } from "@/common/pdUtils";

export class FilesService extends SubscriberService<FilesService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return filesServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public constructor() {
        super();
    }

    public async GetFile(url: string) {
        const response = await blazorServiceInstance.DotNetServiceRef.invokeMethodAsync("GetFile", url);

        this._UpdateSubscribers();
        return response;
    }

    public async GetFileExists(url: string) {
        const response = await blazorServiceInstance.DotNetServiceRef.invokeMethodAsync("GetFileExists", url);
        return response as boolean;
    }

    public async SaveFile(url: string, fileContent: any, trNumber?: string, dontAlert?: boolean) {
        trNumber ??= ZenDataService.Get(null).GetArtworkTrNumber();
        if (!trNumber || trNumber.length === 0) {
            trNumber = "0";
        }

        if(typeof fileContent === "object") {
            fileContent = JSON.stringify(fileContent);
        }

        const response = await fetch(`/api/file?url=${encodeURIComponent(url)}&trNumber=${trNumber}`, {
            method: "POST",
            mode: "cors",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/octet-stream"
            },
            body: fileContent
        });

        if (!response.ok) {
            console.error(response);
            throw new Error(`Error: Could not save file to path ${url}!`);
        }

        this._UpdateSubscribers();
        return response;
    }

    public async GetFileLastModified(url: string) {
        const response = await blazorServiceInstance.DotNetServiceRef.invokeMethodAsync("GetFileLastModified", url);
        return response;
    }

    public startDownload(fileContent: any, fileName: string = "Download", mimeType: string = "application/octet-stream") {
        const blobBuilder = (window as any).BlobBuilder || (window as any).WebKitBlobBuilder;

        const a = window.document.createElement("a");
        a.download = fileName;

        let blob = null;
        if (blobBuilder) {
            blob = new blobBuilder();
            blob.append(fileContent);
            a.href = window.URL.createObjectURL(blob.getBlob(mimeType));
        } else {
            blob = new Blob([fileContent], { "type": mimeType });
            a.href = window.URL.createObjectURL(blob);
        }

        a.dataset.downloadurl = [mimeType, a.download, a.href].join(":");
        a.click();
        a.remove();
    }

    public async openArtworkFromComputer(ev: any) { // TODO: Fix this type
        // getting a hold of the file reference
        const target = ev?.target as HTMLInputElement;
        if(!target) {
            throw new Error("Could not get target element for file input.");
        }

        const file = target.files[0];
        if(!target) {
            throw new Error("Could not get file from file input.");
        }

        const openFileFromComputerInput = document.getElementById("__ZAA_OpenPanelFromComputerInput");
        if(openFileFromComputerInput) {
            openFileFromComputerInput.value = null;
        }

        const res = await blazorConfirm("Open New File", "<p>Do you want to open a new file?</p><p>This will also erase your undo history!</p>");
        if (!res) {
            return;
        }

        console.log(`Opening SVG '${file.name}' as new document.`);

        // setting up the reader
        const reader = new FileReader();
        reader.readAsText(file, 'UTF-8');
        // here we tell the reader what to do when it's done reading...
        reader.addEventListener('load', (readerEvent) => {
            svgEditServiceInstance.editor.loadSvgWithResolver(readerEvent.target.result)
                .catch(err => {
                    console.error(err);
                }
            );
        });
    }

    public async saveCurrentArtworkToComputer(fileName: string) {
        await coloursServiceInstance.SaveSpotColours();

        let saveName = `${fileName}.svg`;
        let svg = svgEditServiceInstance.canvas.getSvgString();
        svg = '<?xml version="1.0"?>\n' + svg;

        startDownloadContent(saveName, svg, "image/svg+xml");
    }
}

export const filesServiceInstance = createStaticServiceInstance(new FilesService(), "filesService");

// For React Functional Components
export const useFilesService = createUseZenAutoArtService(filesServiceInstance);
